import React, { useEffect, useState, useMemo, useCallback } from "react";
import API from "../services/api";
import { toast } from "react-toastify";
import AdminTable from "../components/AdminTable";
import ApplicationModal from "../components/ApplicationModal";
import StatsChart from "../components/StatsChart";
import AuditTable from "../components/AuditTable";
import {
  CheckCircle, XCircle, Clock, FileText, Search,
  RefreshCw, Download, ChevronLeft, ChevronRight, X,
} from "lucide-react";

/* ── Rejection modal (replaces window.prompt) ────────────────────────────── */
const RejectModal = ({ appId, onClose, onConfirm }) => {
  const [remarks, setRemarks] = useState("");
  const [saving,  setSaving]  = useState(false);
  if (!appId) return null;

  const handleConfirm = async () => {
    if (!remarks.trim()) { toast.error("Please provide a reason for rejection"); return; }
    setSaving(true);
    await onConfirm(appId, remarks.trim());
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-900">Reject Application</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Provide a clear reason. This will be sent to the student by email.
        </p>
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={4}
          placeholder="e.g. Incomplete documents — fee structure not uploaded."
          className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition resize-none mb-4"
        />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white hover:bg-red-700 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Reject & Notify
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main dashboard ──────────────────────────────────────────────────────── */
const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [auditLogs,    setAuditLogs]    = useState([]);
  const [wards,        setWards]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [totalPages,   setTotalPages]   = useState(1);
  const [sortField,    setSortField]    = useState("createdAt");
  const [sortOrder,    setSortOrder]    = useState("desc");
  const [selectedApp,  setSelectedApp]  = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [page,         setPage]         = useState(1);
  const [visible,      setVisible]      = useState(false);

  const [filters, setFilters] = useState({
    wardName: "", schoolLevel: "", status: "", search: "",
  });

  // ── Fetch wards for filter dropdown ──────────────────────────────────────
  useEffect(() => {
    API.get("/wards")
      .then((res) => setWards(res.data))
      .catch(() => {});
  }, []);

  // ── Fetch applications ────────────────────────────────────────────────────
  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get("/admin/applications", {
        params: {
          status:      filters.status      || undefined,
          schoolLevel: filters.schoolLevel || undefined,
          search:      filters.search      || undefined,
          page,
          limit: 10,
          sortField,
          sortOrder,
        },
      });
      setApplications(res.data.data || []);
      setTotalPages(res.data.pages  || 1);
    } catch {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [filters, page, sortField, sortOrder]);

  // ── Fetch audit logs ──────────────────────────────────────────────────────
  const fetchAuditLogs = useCallback(async () => {
    try {
      const res = await API.get("/admin/audit-logs");
      setAuditLogs(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchAuditLogs();
    setTimeout(() => setVisible(true), 60);
  }, [fetchApplications, fetchAuditLogs]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [filters]);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortOrder("asc"); }
  };

  // ── Approve ───────────────────────────────────────────────────────────────
  const approve = async (id) => {
    try {
      await API.post(`/admin/applications/${id}/approve`);
      toast.success("Application approved — student notified");
      fetchApplications(); fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approval failed");
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const reject = async (id, remarks) => {
    try {
      await API.post(`/admin/applications/${id}/reject`, { remarks });
      toast.info("Application rejected — student notified");
      setRejectTarget(null);
      fetchApplications(); fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Rejection failed");
    }
  };

  // ── Bulk approve ──────────────────────────────────────────────────────────
  const bulkApprove = async () => {
    if (!window.confirm("Approve ALL currently submitted applications? Students will be notified by email.")) return;
    try {
      const res = await API.post("/admin/applications/approve-all");
      toast.success(res.data.message);
      fetchApplications(); fetchAuditLogs();
    } catch { toast.error("Bulk approval failed"); }
  };

  // ── Client-side ward filter (server doesn't know ward name strings) ───────
  const filteredApplications = useMemo(() => {
    if (!filters.wardName) return applications;
    return applications.filter((app) => {
      const wn = app.studentId?.ward?.name || "";
      return wn.toLowerCase().includes(filters.wardName.toLowerCase());
    });
  }, [applications, filters.wardName]);

  // ── Stats from current page data ──────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     applications.length,
    submitted: applications.filter((a) => a.status === "SUBMITTED").length,
    approved:  applications.filter((a) => a.status === "APPROVED").length,
    rejected:  applications.filter((a) => a.status === "REJECTED").length,
  }), [applications]);

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = applications.map((a) => [
      `"${a.studentId?.fullName || ""}"`,
      `"${a.studentId?.ward?.name || ""}"`,
      `"${a.studentId?.schoolLevel || ""}"`,
      `"${a.institution?.institutionName || ""}"`,
      `"${a.institution?.courseOrClass || ""}"`,
      a.fees?.totalFees || 0,
      a.fees?.feeBalance || 0,
      a.status,
      a.academicYear,
    ].join(","));

    const header = "Student,Ward,Level,Institution,Course,Total Fees,Fee Balance,Status,Academic Year";
    const csv    = [header, ...rows].join("\n");
    const blob   = new Blob([csv], { type: "text/csv" });
    const link   = document.createElement("a");
    link.href    = window.URL.createObjectURL(blob);
    link.download = `applications_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const statCards = [
    { title: "Total",     value: stats.total,     icon: FileText,    color: "bg-blue-50 text-primary",     border: "border-blue-100" },
    { title: "Submitted", value: stats.submitted,  icon: Clock,       color: "bg-yellow-50 text-yellow-600", border: "border-yellow-100" },
    { title: "Approved",  value: stats.approved,   icon: CheckCircle, color: "bg-green-50 text-green-600",  border: "border-green-100" },
    { title: "Rejected",  value: stats.rejected,   icon: XCircle,     color: "bg-red-50 text-red-600",      border: "border-red-100" },
  ];

  return (
    <div className="space-y-6">

      {/* Title */}
      <div style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.5s" }}>
        <h2 className="text-3xl font-bold text-primary">CDF Admin Dashboard</h2>
        <p className="text-gray-500 text-sm mt-1">Manage student bursary applications and approvals</p>
      </div>

      {/* Stat cards */}
      <div className="grid md:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div
            key={i}
            className={`bg-white rounded-2xl shadow-sm p-5 flex items-center justify-between border ${s.border} hover:shadow-md hover:-translate-y-1 transition-all duration-300`}
            style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.5s ease ${i * 0.08}s` }}
          >
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">{s.title}</p>
              <h3 className="text-3xl font-bold text-gray-800">{s.value}</h3>
            </div>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${s.color}`}>
              <s.icon size={22} />
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-2xl p-4 flex flex-wrap gap-3 items-center border border-gray-100">

        {/* Search */}
        <div className="flex items-center border-2 border-gray-200 rounded-xl px-3 py-2 focus-within:border-primary transition min-w-[180px]">
          <Search size={14} className="text-gray-400 mr-2 flex-shrink-0" />
          <input
            placeholder="Search student..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="outline-none text-sm bg-transparent w-full"
          />
        </div>

        {/* Ward dropdown */}
        <select
          value={filters.wardName}
          onChange={(e) => setFilters({ ...filters, wardName: e.target.value })}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary transition"
        >
          <option value="">All Wards</option>
          {wards.map((w) => (
            <option key={w._id} value={w.name}>{w.name}</option>
          ))}
        </select>

        {/* School level */}
        <select
          value={filters.schoolLevel}
          onChange={(e) => setFilters({ ...filters, schoolLevel: e.target.value })}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary transition"
        >
          <option value="">All Levels</option>
          <option value="SECONDARY">Secondary</option>
          <option value="COLLEGE">College</option>
          <option value="UNIVERSITY">University</option>
        </select>

        {/* Status */}
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary transition"
        >
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>

        {/* Actions */}
        <div className="flex gap-2 ml-auto flex-wrap">
          <button onClick={fetchApplications} className="bg-secondary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-700 active:scale-95 transition">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={bulkApprove} className="bg-primary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-800 active:scale-95 transition">
            <CheckCircle size={14} /> Bulk Approve
          </button>
          <button onClick={exportCSV} className="bg-gray-700 text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-gray-800 active:scale-95 transition">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        {loading ? (
          <div className="flex flex-col items-center py-14 gap-3">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading applications...</p>
          </div>
        ) : (
          <>
            <AdminTable
              applications={filteredApplications}
              onApprove={approve}
              onReject={(id) => setRejectTarget(id)}
              onSort={handleSort}
              onView={(app) => setSelectedApp(app)}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                <p className="text-xs text-gray-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-gray-200 text-gray-500 hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = page <= 3 ? i + 1 : page - 2 + i;
                    if (p < 1 || p > totalPages) return null;
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition
                          ${p === page ? "bg-primary text-white border-2 border-primary" : "border-2 border-gray-200 text-gray-600 hover:border-primary hover:text-primary"}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    disabled={page === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-gray-200 text-gray-500 hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Charts */}
      <StatsChart stats={stats} />

      {/* Audit logs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <AuditTable auditLogs={auditLogs} />
      </div>

      {/* Modals */}
      <ApplicationModal app={selectedApp} onClose={() => setSelectedApp(null)} />
      <RejectModal
        appId={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={reject}
      />
    </div>
  );
};

export default AdminDashboard;
